import { useState, useRef, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import axios from 'axios';
import { Calendar, Clock, MapPin, User, Mail, Phone, CheckCircle, XCircle, Loader2, Lock, Edit2, ArrowLeft, Heart, AlertTriangle, Maximize, PenTool, RotateCcw, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatPhoneNumber } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClassSignIn = () => {
  // Check for app mode (bypasses password) - uses secret app token
  const urlParams = new URLSearchParams(window.location.search);
  const appToken = urlParams.get('appkey');
  const isAppMode = appToken === 'BLCPR2024';
  
  // Fullscreen kiosk mode state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exitTapCount, setExitTapCount] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const exitTapTimeout = useRef(null);
  
  // Handle fullscreen change events and disable right-click in kiosk mode
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) {
        setShowExitConfirm(false);
      }
    };
    
    // Disable right-click context menu in fullscreen mode
    const handleContextMenu = (e) => {
      if (document.fullscreenElement) {
        e.preventDefault();
        return false;
      }
    };
    
    // Disable Escape key from exiting fullscreen
    const handleKeyDown = (e) => {
      if (document.fullscreenElement && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);
  
  // Enter fullscreen mode
  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }, []);
  
  // Exit fullscreen mode
  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    setShowExitConfirm(false);
  }, []);
  
  // Handle triple-tap in corner to show exit confirmation
  const handleExitTap = useCallback(() => {
    if (!isFullscreen) return;
    
    setExitTapCount(prev => {
      const newCount = prev + 1;
      
      // Clear previous timeout
      if (exitTapTimeout.current) {
        clearTimeout(exitTapTimeout.current);
      }
      
      // If 3 taps, show confirmation dialog
      if (newCount >= 3) {
        setShowExitConfirm(true);
        return 0;
      }
      
      // Reset count after 1 second of no taps
      exitTapTimeout.current = setTimeout(() => {
        setExitTapCount(0);
      }, 1000);
      
      return newCount;
    });
  }, [isFullscreen]);
  
  // Password state
  const [showPasswordDialog, setShowPasswordDialog] = useState(!isAppMode);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [checkingPassword, setCheckingPassword] = useState(false);
  const [authenticated, setAuthenticated] = useState(isAppMode);

  // Lookup state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [multipleMatches, setMultipleMatches] = useState([]);

  // Form state
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Signature state
  const sigCanvas = useRef(null);
  const [signatureData, setSignatureData] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  // Check for valid session on mount
  useEffect(() => {
    const checkSession = () => {
      const sessionData = localStorage.getItem('signin_session');
      if (sessionData) {
        try {
          const { expiry } = JSON.parse(sessionData);
          if (expiry && Date.now() < expiry) {
            // Session still valid
            setAuthenticated(true);
            setShowPasswordDialog(false);
            return;
          } else {
            // Session expired, clear it
            localStorage.removeItem('signin_session');
          }
        } catch (e) {
          localStorage.removeItem('signin_session');
        }
      }
    };
    checkSession();
  }, []);

  // Verify password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setCheckingPassword(true);

    try {
      const response = await axios.post(`${API}/signin/verify-password`, { password });
      if (response.data.success) {
        // Store session with 1 hour expiry
        const expiry = Date.now() + (60 * 60 * 1000); // 1 hour from now
        localStorage.setItem('signin_session', JSON.stringify({ expiry }));
        
        setAuthenticated(true);
        setShowPasswordDialog(false);
        toast.success('Access granted for 1 hour');
      } else {
        setPasswordError('Incorrect password');
      }
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Incorrect password');
    } finally {
      setCheckingPassword(false);
    }
  };

  // Look up student
  const handleLookup = async (e) => {
    e.preventDefault();
    if (!firstName.trim() && !lastName.trim() && !searchPhone.trim()) {
      toast.error('Please enter your name or phone number');
      return;
    }

    setLookupLoading(true);
    setLookupError('');
    setStudentData(null);
    setMultipleMatches([]);

    try {
      const response = await axios.post(`${API}/signin/lookup-student`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: searchPhone.trim()
      });

      if (response.data.multiple_matches) {
        setMultipleMatches(response.data.matches);
        toast.info(`Found ${response.data.matches.length} matching students. Please select your name.`);
      } else if (response.data.student) {
        const student = response.data.student;
        setStudentData(student);
        setEditedFirstName(student.first_name || '');
        setEditedLastName(student.last_name || '');
        setEditedEmail(student.email || '');
        setEditedPhone(student.phone || '');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'No class found for today with this student name';
      setLookupError(msg);
    } finally {
      setLookupLoading(false);
    }
  };

  // Select specific match when multiple found
  const handleSelectMatch = (match) => {
    setStudentData(match);
    setEditedFirstName(match.first_name || '');
    setEditedLastName(match.last_name || '');
    setEditedEmail(match.email || '');
    setEditedPhone(match.phone || '');
    setMultipleMatches([]);
  };

  // Clear signature
  const clearSignature = () => {
    const canvas = sigCanvas.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  // Canvas drawing handlers for full-screen signature
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = sigCanvas.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = sigCanvas.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Initialize canvas with white background when modal opens
  useEffect(() => {
    if (showSignatureModal && sigCanvas.current) {
      const canvas = sigCanvas.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [showSignatureModal]);

  // Open signature modal
  const openSignatureModal = () => {
    setShowSignatureModal(true);
  };

  // Save signature from modal
  const saveSignature = () => {
    const canvas = sigCanvas.current;
    if (canvas) {
      // Check if canvas has any drawing (not just white)
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let hasDrawing = false;
      for (let i = 0; i < data.length; i += 4) {
        // Check if any pixel is not white
        if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
          hasDrawing = true;
          break;
        }
      }
      
      if (hasDrawing) {
        setSignatureData(canvas.toDataURL('image/png'));
        setShowSignatureModal(false);
        toast.success('Signature captured');
      } else {
        toast.error('Please sign before continuing');
      }
    }
  };

  // Check if signature is empty - removed old handler
  const handleSignatureEnd = () => {
    // This is now handled by saveSignature
  };

  // Submit sign-in
  const handleSubmit = async () => {
    // Validation
    if (!editedFirstName.trim() || !editedLastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    if (!editedEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    if (!signatureData) {
      toast.error('Please provide your signature');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/signin/submit`, {
        booking_id: studentData.booking_id,
        student_index: studentData.student_index,
        first_name: editedFirstName.trim(),
        last_name: editedLastName.trim(),
        email: editedEmail.trim(),
        phone: editedPhone.trim(),
        signature: signatureData,
        terms_accepted: termsAccepted
      });

      if (response.data.success) {
        setSubmitSuccess(true);
        toast.success('Successfully signed in!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit sign-in');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset to lookup another student
  const handleReset = () => {
    setFirstName('');
    setLastName('');
    setStudentData(null);
    setLookupError('');
    setMultipleMatches([]);
    setEditedFirstName('');
    setEditedLastName('');
    setEditedEmail('');
    setEditedPhone('');
    setTermsAccepted(false);
    setSignatureData(null);
    setSubmitSuccess(false);
  };

  // Password Dialog
  if (!authenticated) {
    return (
      <div 
        className="min-h-screen relative flex items-center justify-center p-4"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
        <Card className="w-full max-w-md bg-slate-800/90 backdrop-blur-md border-slate-700 p-8 relative z-10">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="Biggest Little CPR" className="h-20 w-auto mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Class Sign-In</h1>
            <p className="text-slate-400">Enter the access password to continue</p>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-slate-700 border-slate-600 text-white"
                  autoFocus
                />
                {passwordError && (
                  <p className="text-red-400 text-sm mt-2">{passwordError}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={checkingPassword || !password}
                className="w-full bg-teal-600 hover:bg-teal-500"
              >
                {checkingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </form>

          {!isAppMode && (
            <div className="mt-6 text-center">
              <a href="/" className="text-teal-400 hover:text-teal-300 text-sm">
                <ArrowLeft className="h-4 w-4 inline mr-1" />
                Back to Booking Page
              </a>
            </div>
          )}
        </Card>
      </div>
    );
  }

  // Success Screen
  if (submitSuccess) {
    return (
      <div 
        className="min-h-screen relative flex items-center justify-center p-4"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
        <Card className="w-full max-w-md bg-slate-800/90 backdrop-blur-md border-slate-700 p-8 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-900/50 mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Successfully Signed In!</h1>
          <p className="text-slate-400 mb-6">
            Thank you, {editedFirstName}. You're all set for your class today.
          </p>
          <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
            <p className="text-teal-400 font-medium">{studentData?.class_type}</p>
            <p className="text-slate-300 text-sm">{studentData?.class_date} • {studentData?.class_time}</p>
          </div>
          <Button onClick={handleReset} variant="outline" className="border-teal-600 text-teal-400">
            Sign In Another Student
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url('https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
      
      {/* Hidden exit zone - tap 3 times in upper right to exit fullscreen */}
      {isFullscreen && (
        <div 
          className="fixed top-0 right-0 w-24 h-24 z-[100] cursor-default"
          onClick={handleExitTap}
          onTouchEnd={handleExitTap}
        />
      )}
      
      {/* Exit Fullscreen Confirmation Dialog */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 mx-4 max-w-sm shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-3">Exit Kiosk Mode?</h3>
            <p className="text-slate-400 mb-6">Are you sure you want to exit fullscreen kiosk mode?</p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowExitConfirm(false)}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={exitFullscreen}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Exit Fullscreen
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <nav className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Biggest Little CPR" className="h-12 w-auto" />
              <h1 className="text-xl font-bold text-white">Class Sign-In</h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Fullscreen button - only show when not in fullscreen */}
              {!isFullscreen && (
                <Button
                  onClick={enterFullscreen}
                  variant="outline"
                  size="sm"
                  className="border-teal-600 text-teal-400 hover:bg-teal-900/30"
                  title="Enter Kiosk Mode"
                >
                  <Maximize className="h-4 w-4 mr-2" />
                  Kiosk Mode
                </Button>
              )}
              {!isAppMode && !isFullscreen && (
                <a href="/" className="text-teal-400 hover:text-teal-300 text-sm flex items-center">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Booking
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8 max-w-2xl relative z-10">
        {/* Student Lookup Form */}
        {!studentData && multipleMatches.length === 0 && (
          <Card className="bg-slate-800 border-slate-700 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-2">Find Your Class</h2>
            <p className="text-slate-400 mb-6">Enter your name or phone number to sign in for today's class</p>

            <form onSubmit={handleLookup} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-slate-300">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John or Jo"
                    className="bg-slate-700 border-slate-600 text-white"
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-slate-300">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe or D"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-600"></span>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-slate-800 px-2 text-slate-500">or search by phone</span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="searchPhone" className="text-slate-300">Phone Number</Label>
                <Input
                  id="searchPhone"
                  type="tel"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(formatPhoneNumber(e.target.value))}
                  placeholder="(555) 123-4567"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <p className="text-xs text-slate-500">
                Tip: You can search with partial names or your phone number
              </p>

              {lookupError && (
                <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">Student Not Found</p>
                    <p className="text-red-300 text-sm">{lookupError}</p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={lookupLoading || (!firstName.trim() && !lastName.trim() && !searchPhone.trim())}
                className="w-full bg-teal-600 hover:bg-teal-500"
              >
                {lookupLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  'Sign-in'
                )}
              </Button>
            </form>
          </Card>
        )}

        {/* Multiple Matches Selection */}
        {multipleMatches.length > 0 && (
          <Card className="bg-slate-800 border-slate-700 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-2">Select Your Name</h2>
            <p className="text-slate-400 mb-6">We found {multipleMatches.length} matching students. Please select yourself:</p>

            <div className="space-y-3">
              {multipleMatches.map((match, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectMatch(match)}
                  className="w-full text-left bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-teal-500 rounded-lg p-4 transition-colors"
                >
                  <p className="text-white font-medium text-lg">{match.first_name} {match.last_name}</p>
                  <p className="text-teal-400 text-sm">{match.class_type}</p>
                  <p className="text-slate-300 text-sm">{match.class_date} • {match.class_time}</p>
                  <p className="text-slate-400 text-sm flex items-center mt-1">
                    <MapPin className="h-3 w-3 mr-1" />
                    {match.location}
                  </p>
                  {match.signed_in && (
                    <p className="text-yellow-400 text-xs mt-2 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Already signed in
                    </p>
                  )}
                </button>
              ))}
            </div>

            <Button
              onClick={() => { setMultipleMatches([]); setFirstName(''); setLastName(''); setSearchPhone(''); }}
              variant="outline"
              className="w-full mt-4 border-slate-600 text-slate-300"
            >
              Search Again
            </Button>
          </Card>
        )}

        {/* Student Information Form */}
        {studentData && (
          <div className="space-y-6">
            {/* Class Info Card */}
            <Card className="bg-slate-800 border-slate-700 p-6">
              <div className="flex items-center gap-2 text-teal-400 mb-4">
                <Calendar className="h-5 w-5" />
                <h3 className="font-semibold">Class Information</h3>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <p className="text-white font-medium text-lg">{studentData.class_type}</p>
                <div className="flex items-center gap-4 text-slate-300 text-sm">
                  <span className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1 text-slate-400" />
                    {studentData.class_date}
                  </span>
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-slate-400" />
                    {studentData.class_time}
                  </span>
                </div>
                <p className="flex items-center text-slate-400 text-sm">
                  <MapPin className="h-4 w-4 mr-1" />
                  {studentData.location}
                </p>
              </div>
            </Card>

            {/* Student Info Form */}
            <Card className="bg-slate-800 border-slate-700 p-6">
              <div className="flex items-center gap-2 text-teal-400 mb-4">
                <User className="h-5 w-5" />
                <h3 className="font-semibold">Your Information</h3>
              </div>
              <p className="text-sm mb-4">
                <span className="text-slate-300">Please verify and update your information if needed</span>
                <span className="block mt-1 text-amber-400 font-bold">(This is exactly how your certificate/card will show)</span>
              </p>
              
              {/* Important Email Disclosure */}
              <div className="bg-amber-900/40 border border-amber-600/50 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <Mail className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-200 font-semibold mb-1">Important: Email Address Notice</p>
                    <p className="text-amber-100/90">
                      This email will be used for:
                    </p>
                    <ul className="text-amber-100/80 mt-1 space-y-0.5 list-disc list-inside ml-1">
                      <li><strong>Digital Testing</strong> (if applicable) - must have access during class</li>
                      <li><strong>Your Certification Card / eCard</strong></li>
                      <li><strong>Course Materials & eBooks</strong></li>
                      <li><strong>All future communications</strong></li>
                    </ul>
                    <p className="text-amber-200 mt-2 font-medium">
                      Please ensure your email is correct and accessible!
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="editFirstName" className="text-slate-300">First Name</Label>
                    <Input
                      id="editFirstName"
                      value={editedFirstName}
                      onChange={(e) => setEditedFirstName(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="editLastName" className="text-slate-300">Last Name</Label>
                    <Input
                      id="editLastName"
                      value={editedLastName}
                      onChange={(e) => setEditedLastName(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="editEmail" className="text-slate-300">Email</Label>
                  <Input
                    id="editEmail"
                    type="email"
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>

                <div>
                  <Label htmlFor="editPhone" className="text-slate-300">Phone Number</Label>
                  <Input
                    id="editPhone"
                    type="tel"
                    value={editedPhone}
                    onChange={(e) => setEditedPhone(formatPhoneNumber(e.target.value))}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
            </Card>

            {/* Terms and Signature */}
            <Card className="bg-slate-800 border-slate-700 p-6">
              <div className="flex items-center gap-2 text-teal-400 mb-4">
                <Edit2 className="h-5 w-5" />
                <h3 className="font-semibold">Agreement & Signature</h3>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 mb-6">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-1 border-slate-500 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                />
                <label htmlFor="terms" className="text-slate-300 text-sm leading-relaxed cursor-pointer space-y-3">
                  <p>I hereby confirm that I have reviewed all information provided and affirm that it is true, complete, and accurate to the best of my knowledge. I understand that the information submitted will be used for the issuance of my certification card and/or certificate.</p>
                  
                  <p>I acknowledge and agree that I am required to attend and complete the entire course in its entirety and successfully pass all required assessments in order to be eligible for certification. I further understand and agree that failure to complete the class, including leaving the course early for any reason, will result in forfeiture of certification and that no refunds will be issued in accordance with the applicable booking terms and conditions.</p>
                  
                  <p>I also acknowledge that, upon signing in for my class, I will receive the applicable eBook via email from the American Heart Association (AHA), if required. I understand and consent that the email address provided will be used for the delivery of course materials, as well as for the issuance of any certification cards and/or certificates.</p>
                </label>
              </div>

              {/* Signature Section - Tap to Sign Button */}
              {termsAccepted && (
                <div className="space-y-3">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <PenTool className="h-4 w-4" />
                    Your Signature
                  </Label>
                  
                  {signatureData ? (
                    // Show signature preview
                    <div 
                      className="border-2 border-green-500/50 rounded-lg bg-white p-2 cursor-pointer hover:border-green-400 transition-colors"
                      onClick={openSignatureModal}
                    >
                      <img 
                        src={signatureData} 
                        alt="Your signature" 
                        className="w-full h-[80px] object-contain"
                      />
                      <p className="text-green-600 text-xs text-center mt-1 flex items-center justify-center gap-1">
                        <Check className="h-3 w-3" /> Signature captured - tap to re-sign
                      </p>
                    </div>
                  ) : (
                    // Show button to open signature modal
                    <Button
                      type="button"
                      variant="outline"
                      onClick={openSignatureModal}
                      className="w-full h-24 border-2 border-dashed border-slate-500 bg-slate-700/30 hover:bg-slate-700/50 hover:border-teal-500 text-slate-300 flex flex-col items-center justify-center gap-2"
                    >
                      <PenTool className="h-8 w-8" />
                      <span>Tap to Sign</span>
                    </Button>
                  )}
                </div>
              )}
            </Card>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Start Over
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !termsAccepted || !signatureData}
                className="flex-1 bg-teal-600 hover:bg-teal-500"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Complete Sign-In
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
        
        {/* Full-Screen Signature Modal */}
        <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-none w-screen h-screen m-0 p-0 rounded-none flex flex-col">
            <div className="bg-slate-800 p-4 flex items-center justify-between border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <PenTool className="h-6 w-6 text-teal-400" />
                <div>
                  <h2 className="text-white text-lg font-semibold">Sign Below</h2>
                  <p className="text-slate-400 text-sm">Use your finger or stylus to sign</p>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowSignatureModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="flex-1 p-4 min-h-0">
              <div className="h-full bg-white rounded-lg overflow-hidden relative">
                <canvas
                  ref={sigCanvas}
                  width={1200}
                  height={500}
                  className="w-full h-full cursor-crosshair touch-none"
                  style={{ touchAction: 'none' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {/* Sign here hint */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                  <p className="text-slate-400 text-2xl font-light">Sign Here</p>
                </div>
              </div>
            </div>
            
            {/* Footer with extra bottom padding for device navigation buttons */}
            <div className="bg-slate-800 p-4 pb-8 flex items-center justify-between border-t border-slate-700 flex-shrink-0" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}>
              <Button
                variant="outline"
                onClick={clearSignature}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 h-12 px-6"
              >
                <RotateCcw className="h-5 w-5 mr-2" />
                Clear
              </Button>
              <Button
                onClick={saveSignature}
                className="bg-teal-600 hover:bg-teal-500 px-8 h-12 text-lg"
              >
                <Check className="h-5 w-5 mr-2" />
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ClassSignIn;
